import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { translations } from "../i18n/translations";

// /auth/verify?token=...  — top-level, no language prefix.
// Language is read from localStorage / browser; English by default.
function pickLang() {
  const stored = typeof localStorage !== "undefined" ? localStorage.getItem("lp_lang") : null;
  if (stored === "en" || stored === "ro") return stored;
  if (typeof navigator !== "undefined" && (navigator.language || "").toLowerCase().startsWith("ro")) return "ro";
  return "en";
}

const COPY = {
  en: {
    headline: "Verifying your email…",
    sub: "Hold tight — confirming your account.",
    success: "Your email has been verified.",
    successSub: "Welcome to The Life Plot.",
    cta: "Open your dashboard",
    expired: "This verification link has expired. Please request a new one.",
    invalid: "This verification link is invalid.",
    requestNew: "Request a new link",
    backHome: "Back to home",
  },
  ro: {
    headline: "Confirmăm emailul tău…",
    sub: "Un moment — confirmăm contul tău.",
    success: "Emailul tău a fost confirmat.",
    successSub: "Bine ai venit la The Life Plot.",
    cta: "Deschide contul",
    expired: "Acest link de confirmare a expirat. Te rugăm să soliciți unul nou.",
    invalid: "Acest link de confirmare nu este valid.",
    requestNew: "Solicită un link nou",
    backHome: "Înapoi acasă",
  },
};

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { verifyEmail, user } = useAuth();
  const [status, setStatus] = useState("loading"); // loading | success | expired | invalid
  const [lang, setLang] = useState(pickLang());

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    (async () => {
      const res = await verifyEmail(token);
      if (res.ok) {
        setStatus("success");
        // user.language now reflects backend; refresh local lang
        if (user && user.language) setLang(user.language);
      } else if (res.code === "expired_token") setStatus("expired");
      else setStatus("invalid");
    })();
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    if (user && user.language) setLang(user.language);
  }, [user]);

  const c = COPY[lang] || COPY.en;

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="glass rounded-3xl p-10 md:p-14 w-full max-w-xl text-center"
      >
        {status === "loading" && (
          <>
            <Loader2 className="h-10 w-10 text-gold mx-auto animate-spin" />
            <h1 className="mt-8 font-display text-3xl tracking-tight">{c.headline}</h1>
            <p className="mt-3 text-cream-muted">{c.sub}</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center">
              <CheckCircle2 className="h-7 w-7 text-gold" strokeWidth={1.75} />
            </div>
            <p className="mt-6 font-mono-label text-gold/80">{translations[lang].brand.toUpperCase()}</p>
            <h1
              data-testid="verify-success"
              className="mt-3 font-display text-4xl md:text-5xl tracking-tight leading-[1.05]"
            >
              {c.success}
            </h1>
            <p className="mt-5 text-cream-muted text-lg">{c.successSub}</p>
            <Link
              to={`/${lang}/dashboard`}
              data-testid="verify-success-cta"
              className="btn btn-gold btn-lg mt-10 inline-flex"
            >
              {c.cta}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </>
        )}

        {(status === "expired" || status === "invalid") && (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center">
              <AlertCircle className="h-7 w-7 text-red-400" strokeWidth={1.75} />
            </div>
            <h1
              data-testid={`verify-${status}`}
              className="mt-8 font-display text-3xl md:text-4xl tracking-tight"
            >
              {status === "expired" ? c.expired : c.invalid}
            </h1>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <Link to={`/${lang}/check-inbox`} className="btn btn-gold">
                {c.requestNew}
              </Link>
              <Link to={`/${lang}`} className="btn btn-ghost">
                {c.backHome}
              </Link>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
